/**
 * What is wrong with the orders that are not done yet.
 *
 * The Orders page leads with exceptions, not totals: of 188 deliveries, 184
 * delivered is expected; the four that are not is the work. This turns the
 * open legs of a period (and the overdue backlog) into one list per lane,
 * most urgent first, each with the plain-words reason it needs attention.
 *
 * Nothing here invents a status. Every cause comes from a flag or bucket the
 * truth layer already puts on the row (status_bucket, missed_am, at_risk_1700,
 * collection_condition, the delivery-decision rules, the ETA window). The only
 * thing worked out here is the ETA clock, from `eta_window_end`/`eta_at`
 * against the London wall clock, and a row with no ETA is simply never
 * "outside its ETA": unknown stays unknown.
 *
 * Time basis: ETA fields are local wall time (`YYYY-MM-DDTHH:MM:SS`, London),
 * the same basis as the page's other time displays, so "now" is the London
 * wall clock too.
 */

import type { CollectionCondition, OrdersDailyRow } from '../types/ordersDailyTruth';
import {
  COLLECTION_CONDITION_HINTS,
  COLLECTION_CONDITION_LABELS,
  DELIVERY_BUCKET_LABELS,
  COLLECTION_STAGE_LABELS,
  ordersDecisionKind,
  ordersDecisionReason,
  overdueLabel,
} from './ordersDailyPresentation';

export type OrdersExceptionLane = 'deliveries' | 'collections' | 'etas';

/** 0 = open but on track (not an exception). 3 = most urgent. */
export type OrdersExceptionSeverity = 0 | 1 | 2 | 3;

export type OrdersEtaState =
  | { kind: 'expired'; minutesPast: number }
  | { kind: 'expiring'; minutesLeft: number };

export interface OrdersExceptionItem {
  row: OrdersDailyRow;
  lane: OrdersExceptionLane;
  /** Most urgent reason, in plain words. Null when the order is open and on track. */
  cause: string | null;
  severity: OrdersExceptionSeverity;
  /** Every other reason that also applies. */
  alsoCauses: string[];
  /** One line saying why, or where an on-track order currently is. */
  why: string;
  /** "2 days overdue", "ETA passed 1h 52m ago", … */
  age: string | null;
  eta: OrdersEtaState | null;
  /** Expiry of the ETA, local wall time. Undefined when the order has no ETA. */
  etaExpiry?: string;
}

export interface OrdersExceptionLists {
  deliveries: OrdersExceptionItem[];
  collections: OrdersExceptionItem[];
  /** Open deliveries outside their ETA, or expiring within the warning window. */
  etas: OrdersExceptionItem[];
}

/** The London wall clock as `YYYY-MM-DDTHH:MM:SS`, the basis the ETA fields use. */
export function londonWallNow(asOf: Date = new Date()): string {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/London',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(asOf);
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '00';
  return `${get('year')}-${get('month')}-${get('day')}T${get('hour')}:${get('minute')}:${get('second')}`;
}

function wallToMs(wall: string): number | null {
  const m = wall.match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?/);
  if (!m) return null;
  return Date.UTC(+m[1], +m[2] - 1, +m[3], +m[4], +m[5], m[6] ? +m[6] : 0);
}

/** Whole minutes from `fromWall` to `toWall` (negative when `toWall` is earlier). */
export function wallMinutesBetween(fromWall: string, toWall: string): number | null {
  const from = wallToMs(fromWall);
  const to = wallToMs(toWall);
  if (from == null || to == null) return null;
  return Math.round((to - from) / 60_000);
}

/** When the order's ETA runs out. A window's end, or the point estimate. */
export function etaExpiryWall(row: OrdersDailyRow): string | undefined {
  const end = row.eta_window_end?.trim();
  const point = row.eta_at?.trim();
  if (row.eta_basis === 'point') return point || end || undefined;
  return end || point || undefined;
}

/** "45 min", "1h 52m". */
export function formatEtaDuration(minutes: number): string {
  const m = Math.max(0, Math.round(minutes));
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  const rest = m % 60;
  return rest === 0 ? `${h}h` : `${h}h ${String(rest).padStart(2, '0')}m`;
}

function isOpenDelivery(row: OrdersDailyRow): boolean {
  return row.direction === 'delivery' && row.open && !row.delivered;
}

/**
 * Where an open delivery stands against its ETA right now. Null when it has no
 * ETA, is not open, or its ETA is further off than the warning window.
 * `warnMinutes` of 0 turns the "expiring" warning off; "expired" always shows.
 */
export function etaStateFor(
  row: OrdersDailyRow,
  nowWall: string,
  warnMinutes: number,
): OrdersEtaState | null {
  if (!isOpenDelivery(row)) return null;
  const expiry = etaExpiryWall(row);
  if (!expiry) return null;
  const minutes = wallMinutesBetween(nowWall, expiry);
  if (minutes == null) return null;
  if (minutes < 0) return { kind: 'expired', minutesPast: -minutes };
  if (warnMinutes > 0 && minutes <= warnMinutes) return { kind: 'expiring', minutesLeft: minutes };
  return null;
}

interface Cause {
  label: string;
  severity: Exclude<OrdersExceptionSeverity, 0>;
  why: string;
}

const COLLECTION_ATTENTION: Partial<Record<CollectionCondition, 2 | 3>> = {
  needs_investigation: 3,
  overdue: 3,
  at_risk: 2,
};

function overdueAge(row: OrdersDailyRow): string | null {
  if (row.backlog?.kind === 'overdue' || row.backlog?.kind === 'stale') {
    const days = row.backlog.days_overdue;
    if (days != null) return `${days} day${days === 1 ? '' : 's'} overdue`;
  }
  const label = overdueLabel(row.grain_date);
  return label && label !== 'due today' ? label : null;
}

function etaCause(eta: OrdersEtaState, etaExpiry?: string): Cause {
  const at = etaExpiry ? etaExpiry.slice(11, 16) : '';
  const etaText = at ? `ETA ${at}` : 'ETA';
  return eta.kind === 'expired'
    ? { label: 'Outside ETA', severity: 1, why: `${etaText} has passed and it is not delivered` }
    : { label: 'ETA expiring', severity: 1, why: `${etaText} runs out soon and it is not delivered` };
}

function deliveryCauses(row: OrdersDailyRow, eta: OrdersEtaState | null, etaExpiry?: string): Cause[] {
  const causes: Cause[] = [];
  const overdue = row.due_bucket === 'overdue' || row.backlog?.kind === 'overdue';
  if (overdue) {
    causes.push({
      label: overdueAge(row) ?? 'Overdue',
      severity: 3,
      why: 'Open past its due date',
    });
  }
  if (row.status_bucket === 'exception') {
    causes.push({
      label: DELIVERY_BUCKET_LABELS.exception,
      severity: 3,
      why: 'Left the hub with no later scan at a depot. Chase the delivery depot',
    });
  }
  const decision = ordersDecisionKind(row);
  if (decision) {
    causes.push({
      label: decision === 'partial'
        ? 'Part-delivered'
        : decision === 'regressed'
          ? 'POD then re-opened'
          : 'Repeatedly failed',
      severity: decision === 'partial' ? 2 : 3,
      why: ordersDecisionReason(row),
    });
  }
  if (row.missed_am) {
    causes.push({ label: 'Missed by 12:00', severity: 2, why: 'Due by 12:00 and still without a POD' });
  }
  if (row.at_risk_1700) {
    causes.push({ label: 'At risk by 17:00', severity: 2, why: 'Still open with a 17:00 service promise' });
  }
  if (eta) causes.push(etaCause(eta, etaExpiry));
  return causes;
}

function collectionCauses(row: OrdersDailyRow): Cause[] {
  const causes: Cause[] = [];
  if (row.backlog?.kind === 'overdue' || row.due_bucket === 'overdue') {
    causes.push({ label: overdueAge(row) ?? 'Overdue', severity: 3, why: 'Open past its collection date' });
  }
  const condition = row.collection_condition;
  const severity = condition ? COLLECTION_ATTENTION[condition] : undefined;
  if (condition && severity) {
    // The backlog rule above already says "overdue"; do not list it twice.
    if (!(condition === 'overdue' && causes.length > 0)) {
      causes.push({
        label: COLLECTION_CONDITION_LABELS[condition],
        severity,
        why: COLLECTION_CONDITION_HINTS[condition],
      });
    }
  }
  return causes;
}

function onTrackWhy(row: OrdersDailyRow): string {
  if (row.direction === 'collection') {
    if (row.collection_condition === 'expected_exception') {
      return COLLECTION_CONDITION_HINTS.expected_exception;
    }
    return row.collection_stage ? COLLECTION_STAGE_LABELS[row.collection_stage] : 'Open';
  }
  return DELIVERY_BUCKET_LABELS[row.status_bucket] ?? 'Open';
}

function toItem(
  row: OrdersDailyRow,
  lane: OrdersExceptionLane,
  causes: Cause[],
  eta: OrdersEtaState | null,
  etaExpiry: string | undefined,
): OrdersExceptionItem {
  const sorted = [...causes].sort((a, b) => b.severity - a.severity);
  const top = sorted[0];
  let age: string | null = null;
  if (eta?.kind === 'expired') age = `ETA passed ${formatEtaDuration(eta.minutesPast)} ago`;
  else if (eta?.kind === 'expiring') age = `ETA expires in ${formatEtaDuration(eta.minutesLeft)}`;
  const overdue = overdueAge(row);
  if (overdue && (!age || top?.severity === 3)) age = overdue;
  return {
    row,
    lane,
    cause: top?.label ?? null,
    severity: top?.severity ?? 0,
    alsoCauses: sorted.slice(1).map((c) => c.label),
    why: top?.why ?? onTrackWhy(row),
    age,
    eta,
    etaExpiry,
  };
}

function minutesKey(item: OrdersExceptionItem): number {
  if (!item.eta) return Number.POSITIVE_INFINITY;
  return item.eta.kind === 'expired' ? -item.eta.minutesPast : item.eta.minutesLeft;
}

function bySeverity(a: OrdersExceptionItem, b: OrdersExceptionItem): number {
  if (b.severity !== a.severity) return b.severity - a.severity;
  const byEta = minutesKey(a) - minutesKey(b);
  if (byEta !== 0 && Number.isFinite(byEta)) return byEta;
  const byName = (a.row.customer ?? '').localeCompare(b.row.customer ?? '');
  return byName !== 0 ? byName : a.row.tracking_id.localeCompare(b.row.tracking_id);
}

export interface BuildOrdersExceptionsInput {
  /** The period's legs. */
  rows: OrdersDailyRow[];
  /** Open legs past their date, set apart from the period (not in `rows`). */
  overdue?: OrdersDailyRow[];
  nowWall: string;
  /** Minutes before an ETA runs out at which to list it as expiring. 0 = off. */
  etaWarnMinutes: number;
  /**
   * False for a period that does not include today: "outside its ETA now" is
   * a statement about the present, so a past day gets no ETA clock at all.
   */
  includeEta?: boolean;
}

/**
 * One list per lane. Deliveries and collections hold EVERY open leg (on-track
 * ones at severity 0) so the lane's open count and its list always agree; the
 * page filters to "needs action" for the default view. ETAs is the subset of
 * open deliveries outside their ETA or about to be.
 */
export function buildOrdersExceptions(input: BuildOrdersExceptionsInput): OrdersExceptionLists {
  const { rows, overdue = [], nowWall, etaWarnMinutes, includeEta = true } = input;
  const seen = new Set<string>();
  const legs: OrdersDailyRow[] = [];
  for (const row of [...rows, ...overdue]) {
    // Judge each leg by its own direction: a delivery row is also marked
    // collected (the consignment was collected before it went out), so a shared
    // check dropped every open delivery on real data (stage, 23 Oct 2026: the
    // Deliveries card said 8 open and the list was empty).
    const done = row.direction === 'delivery' ? row.delivered : row.collected;
    if (!row.open || done) continue;
    const key = `${row.direction}|${row.tracking_id}`;
    if (seen.has(key)) continue;
    seen.add(key);
    legs.push(row);
  }

  const deliveries: OrdersExceptionItem[] = [];
  const collections: OrdersExceptionItem[] = [];
  const etas: OrdersExceptionItem[] = [];

  for (const row of legs) {
    if (row.direction === 'delivery') {
      const etaExpiry = etaExpiryWall(row);
      const eta = includeEta ? etaStateFor(row, nowWall, etaWarnMinutes) : null;
      deliveries.push(toItem(row, 'deliveries', deliveryCauses(row, eta, etaExpiry), eta, etaExpiry));
      if (eta) etas.push(toItem(row, 'etas', [etaCause(eta, etaExpiry)], eta, etaExpiry));
    } else {
      collections.push(toItem(row, 'collections', collectionCauses(row), null, undefined));
    }
  }

  deliveries.sort(bySeverity);
  collections.sort(bySeverity);
  etas.sort((a, b) => {
    // Already outside first (longest ago first), then expiring (soonest first).
    const rank = (i: OrdersExceptionItem) =>
      i.eta?.kind === 'expired' ? -1e6 - i.eta.minutesPast : i.eta?.kind === 'expiring' ? i.eta.minutesLeft : 1e6;
    return rank(a) - rank(b) || a.row.tracking_id.localeCompare(b.row.tracking_id);
  });
  return { deliveries, collections, etas };
}

export function countNeedingAction(items: OrdersExceptionItem[]): number {
  return items.filter((i) => i.severity > 0).length;
}

export function countOutsideEta(items: OrdersExceptionItem[]): number {
  return items.filter((i) => i.eta?.kind === 'expired').length;
}

export function countExpiringEta(items: OrdersExceptionItem[]): number {
  return items.filter((i) => i.eta?.kind === 'expiring').length;
}
